/**
 * Authentication Service
 * Matches mobile app's authService.js patterns exactly
 */

import { normalizeEmailOrPhone } from '../utils/phoneUtils'
import { withApiErrorHandling, buildApiEndpoint } from '../utils/apiHelpers'
import apiClient from '../config/api'

// Login function - matches mobile app exactly
export const login = async (emailOrPhone, password) => {
  const endpoint = buildApiEndpoint('auth/login');
  
  // Normalize the input according to mobile app standards
  const normalizedEmailOrPhone = normalizeEmailOrPhone(emailOrPhone);
  
  return withApiErrorHandling(
    () => apiClient.post(endpoint, {
      email_or_phone: normalizedEmailOrPhone,
      password,
    }),
    {
      defaultValue: null,
      transformData: (data) => {
        return data;
      },
      logErrors: true
    }
  );
};

// Logout function - matches mobile app
export const logout = async () => {
  const endpoint = buildApiEndpoint('auth/logout');
  
  return withApiErrorHandling(
    () => apiClient.post(endpoint),
    {
      defaultValue: true,
      transformData: () => {
        return true;
      },
      logErrors: false // Don't log logout errors
    }
  );
};

// Refresh token function - matches mobile app structure  
export const refreshToken = async () => {
  const endpoint = buildApiEndpoint('auth/refresh');
  
  return withApiErrorHandling(
    () => apiClient.post(endpoint),
    {
      defaultValue: null,
      transformData: (data) => {
        return data;
      },
      logErrors: true
    }
  );
};

// Validate token function
export const validateToken = async () => {
  const endpoint = buildApiEndpoint('auth/me');

  return withApiErrorHandling(
    () => apiClient.get(endpoint),
    {
      defaultValue: null,
      transformData: (data) => data,
      logErrors: true
    }
  );
};

// Google OAuth login function
// Supports both ID token (popup) and access token + user info (redirect)
export const loginWithGoogle = async (tokenOrCredential, userInfo = null) => {
  const endpoint = buildApiEndpoint('auth/google');

  // If userInfo is provided, we're using access token flow (redirect mode)
  // Otherwise, we're using ID token flow (popup mode)
  const payload = userInfo
    ? { access_token: tokenOrCredential, user_info: userInfo }
    : { credential: tokenOrCredential };

  return withApiErrorHandling(
    () => apiClient.post(endpoint, payload),
    {
      defaultValue: null,
      transformData: (data) => data,
      logErrors: true
    }
  );
};

// Google OAuth login with authorization code (redirect flow)
export const loginWithGoogleCode = async (code, redirectUri) => {
  const endpoint = buildApiEndpoint('auth/google/callback');

  return withApiErrorHandling(
    () => apiClient.post(endpoint, { code, redirect_uri: redirectUri }),
    {
      defaultValue: null,
      transformData: (data) => data,
      logErrors: true
    }
  );
};

// Complete profile after Google OAuth (add phone number)
export const completeProfile = async (profileData) => {
  const endpoint = buildApiEndpoint('auth/complete-profile');

  return withApiErrorHandling(
    () => apiClient.post(endpoint, profileData),
    {
      defaultValue: null,
      transformData: (data) => data,
      logErrors: true
    }
  );
};

// Register new account function
export const register = async (registrationData) => {
  const endpoint = buildApiEndpoint('users/register');

  return withApiErrorHandling(
    () => apiClient.post(endpoint, registrationData),
    {
      defaultValue: null,
      transformData: (data) => data,
      logErrors: true
    }
  );
};

// Set password using invite token (for client invites)
export const setPassword = async (inviteToken, password) => {
  const endpoint = buildApiEndpoint('auth/set-password');

  return withApiErrorHandling(
    () => apiClient.post(endpoint, {
      invite_token: inviteToken,
      password
    }),
    {
      defaultValue: null,
      transformData: (data) => data,
      logErrors: true
    }
  );
};

// Export as service object (for backward compatibility with existing web app)
export const authService = {
  login,
  logout,
  refreshToken,
  validateToken,
  loginWithGoogle,
  loginWithGoogleCode,
  completeProfile,
  register,
  setPassword
};